// Copyright (c) 2026 CTO's Club LLC. All rights reserved.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ApiError, authApi } from '@/lib/pillarApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AnchorbaseLogo } from '@/components/branding/AnchorbaseLogo';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { runInBackground } from '@/lib/utils';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

export default function OpenAnchorbasePage() {
    const log = createComponentLogger('OpenAnchorbasePage');
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);
    const [confirmationUrl, setConfirmationUrl] = useState<string | null>(null);
    const pendingUrl = useRef<string | null>(null);
    const issuanceStarted = useRef(false);
    const retirementStarted = useRef(false);

    const retainPendingUrl = (applicationUrl: string) => {
        pendingUrl.current = applicationUrl;
        setConfirmationUrl(applicationUrl);
    };

    const releasePendingUrl = () => {
        pendingUrl.current = null;
        setConfirmationUrl(null);
    };

    const cancelLinkMutation = useMutation({
        mutationFn: async (applicationUrl: string) => {
            await authApi.cancelNativeAppLoginLink({ application_url: applicationUrl });
            return applicationUrl;
        },
        onSuccess: (applicationUrl) => {
            retirementStarted.current = false;
            if (pendingUrl.current === applicationUrl) {
                releasePendingUrl();
            }
            setError(null);
        },
        onError: () => {
            retirementStarted.current = false;
            setError('Anchorbase could not cancel the app sign-in. Try Cancel again.');
            log.warn('Native application login-link retirement failed');
        },
    });

    const retirePendingUrl = () => {
        if (pendingUrl.current !== null && !retirementStarted.current) {
            retirementStarted.current = true;
            cancelLinkMutation.mutate(pendingUrl.current);
        }
    };

    useEffect(() => {
        const retireAbandonedUrl = () => {
            const applicationUrl = pendingUrl.current;
            if (applicationUrl === null) {
                return;
            }
            pendingUrl.current = null;
            void authApi
                .cancelNativeAppLoginLink(
                    { application_url: applicationUrl },
                    { keepalive: true }
                )
                .catch(() => {
                    log.warn('Abandoned native application login-link retirement failed');
                });
        };
        window.addEventListener('pagehide', retireAbandonedUrl);
        return () => {
            window.removeEventListener('pagehide', retireAbandonedUrl);
            retireAbandonedUrl();
        };
    }, []);

    const issueLinkMutation = useMutation({
        mutationFn: authApi.issueNativeAppLoginLink,
        onSuccess: (response) => {
            setError(null);
            if (navigator.userActivation?.isActive !== true) {
                retainPendingUrl(response.application_url);
                return;
            }
            try {
                window.location.assign(response.application_url);
            } catch {
                retainPendingUrl(response.application_url);
                setError(
                    'Your browser did not open Anchorbase. Try again, or continue in the browser.'
                );
                log.warn('Browser rejected the native application navigation');
            }
        },
        onError: (requestError: Error) => {
            setError(
                requestError instanceof ApiError && requestError.status === 400
                    ? 'Choose an organization in Anchorbase Portal, then try again.'
                    : 'Anchorbase could not prepare the app sign-in. Try again in a moment.'
            );
            log.error('Native application login-link issuance failed', requestError);
        },
        onSettled: () => {
            issuanceStarted.current = false;
        },
    });

    const continueInBrowser = () => {
        runInBackground(navigate('/dashboard', { replace: true }), (navigationError) => {
            log.error('Failed to continue to the browser dashboard', navigationError);
        });
    };

    return (
        <>
            <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 p-4">
                <Card className="w-full max-w-md">
                    <CardHeader className="space-y-3 text-center">
                        <CardTitle className="flex justify-center">
                            <AnchorbaseLogo width={280} />
                        </CardTitle>
                        <CardTitle>Continue in Anchorbase</CardTitle>
                        <CardDescription>
                            You are signed in. Open the Anchorbase app to continue there.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {error !== null && (
                            <div
                                role="alert"
                                className="rounded-md bg-destructive/15 p-3 text-sm text-destructive"
                            >
                                {error}
                            </div>
                        )}
                        <Button
                            type="button"
                            className="w-full"
                            disabled={issueLinkMutation.isPending}
                            onClick={() => {
                                if (issuanceStarted.current) {
                                    return;
                                }
                                issuanceStarted.current = true;
                                setError(null);
                                issueLinkMutation.mutate();
                            }}
                        >
                            {issueLinkMutation.isPending
                                ? 'Preparing Anchorbase...'
                                : 'Open Anchorbase App'}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            className="w-full"
                            onClick={continueInBrowser}
                        >
                            Continue in browser
                        </Button>
                        <p className="text-center text-sm text-muted-foreground">
                            If nothing happens, make sure Anchorbase is installed and try again.
                        </p>
                    </CardContent>
                </Card>
            </main>
            <Dialog
                open={confirmationUrl !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        retirePendingUrl();
                    }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Open Anchorbase?</DialogTitle>
                        <DialogDescription>
                            Confirm that you want this browser to open the Anchorbase app.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            disabled={cancelLinkMutation.isPending}
                            onClick={retirePendingUrl}
                        >
                            {cancelLinkMutation.isPending ? 'Canceling...' : 'Cancel'}
                        </Button>
                        {confirmationUrl !== null && !cancelLinkMutation.isPending && (
                            <Button asChild>
                                <a href={confirmationUrl} onClick={releasePendingUrl}>
                                    Open Anchorbase
                                </a>
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
